import { Injectable } from '@angular/core';
import { Observable, of, combineLatest, merge } from 'rxjs';
import { map, tap, mergeMap } from 'rxjs/operators/';


// Services.
import { StorageService } from '../storage/storage.service';
import { FetchService } from '../fetch/fetch.service';

// Services.
import { LoggerService } from '../logger/logger.service';

// Classes.
import { Logger } from '../../classes/logger';
import { Article } from '../../classes/article';
import { IndexItem } from '../../classes/indexItem';
import { RBImage } from '../../classes/image';
import { SearchResults } from '../../classes/searchResults';
import { Interpretations } from '../../classes/interpretations';


// Type of a dictionary, indexed by string, that holds dictionaries of number
// indexed by number.
type ImageIdArray = { [index: string]: { [index: number]: number } }

// Type of a dictionary of articles indexed by article id.
type ArticleDict = { [index: number]: Article };

// Type of a dictionary of images indexed by image id.
type ImageDict = { [index: number]: RBImage };


class RefToken {
  constructor(
    public id: string,
    public label: string,
    public type: string
  ) { }
}


@Injectable({
  providedIn: 'root'
})
export class DataService {

  protected logger: Logger;

  constructor(
    protected storage: StorageService,
    protected fetcher: FetchService,
    loggerSrvc: LoggerService
  ) {
    this.logger = new Logger(loggerSrvc, this.constructor.name);
  }


  // Index methods.
  /////////////////
  /**
   * Index.
   */
  public getIndex(id: string): Observable<IndexItem> {
    // Load index item from cache.
    let indexItem = this.storage.getIndexItem(id);

    if (indexItem) {
      this.logger.log(`Return index "${id}" from storage`, indexItem)
      return of(indexItem);
    }
    else {
      // Load index from api.
      this.logger.log(`Fetch index "${id}" from server`, indexItem)
      return this.fetcher.getIndex(id).pipe(
        map(rawIndex => this.processRawIndexItem(rawIndex))
      );
    }
  }

  protected processRawIndexItem(rawIndexItem: any) {
    return this.processRawIndexItemRecursive(rawIndexItem);
  }

  protected processRawIndexItemRecursive(rawIndexItem: any) {
    let indexItem: IndexItem = null;
    if (rawIndexItem) {
      indexItem = new IndexItem(rawIndexItem);

      // Process children.
      indexItem.children = rawIndexItem.children.map((indexItemChild: any) => this.processRawIndexItemRecursive(indexItemChild));

      // Store index item.
      this.storage.putIndexItem(indexItem);
    }
    return indexItem;
  }



  // Article methods.
  ///////////////////
  public getArticle(id: string): Observable<Article> {
      // Load article from api.
      return this.fetcher.getArticle(id).pipe(
        map(rawArticle => this.processRawArticle(rawArticle))
      );

  }

  public processRawArticle(rawArticle: any) {
    let imageIds: ImageIdArray = {};
    let articles: { [index: number]: Article } = {};


    let article = this.processRawArticleRecursive(rawArticle, imageIds, articles);

    // Add article to all created articles array.
    articles[article.id] = article;


    // Request all images.
    let imageIdsToRetrieve = Object.keys(imageIds);
    let images$ = combineLatest(imageIdsToRetrieve.map(imageId => this.getImage(+imageId)));

    images$.pipe(
      // Convert the list of images ina dictionary of images indexed by image id.
      map(imageArray => {
        let imageDict: ImageDict = {};
        imageArray.forEach(image => imageDict[image.id] = image)
        return imageDict;
      }))
      //Once we have all images let's replace iamge tokens.
      .subscribe(
        imageDict => this.replaceImageTokensInArticles(imageDict, imageIds, articles),
        error => this.processError(error),
      );

    return article
  }


  /**
   * Replaces all image tokens in all articles.
   *
   * @param images: Dictionary with images uesd to replace tokens.
   * @param imageIds: Dictionary with articles list that use each image.
   * @param articles: Dictionary with article objects. Process is done inthose objects.
   *
   */
  protected replaceImageTokensInArticles(images: ImageDict, imageIds: ImageIdArray, articles: ArticleDict): void {
    Object.keys(imageIds).forEach(imageId => {
      Object.keys(imageIds[imageId]).forEach(articleId => {
        let article = articles[+articleId];
        let image = images[+imageId];
        article.text = article.text.replace(`[EXT=${imageId}]`, image.html);
      })
    })
  }


  protected processRawArticleRecursive(rawArticle: any, imageIds: ImageIdArray, articles: ArticleDict): Article {
    let article = new Article(rawArticle);
    articles[article.id] = article;


    // Process children if any.
    if (Array.isArray(rawArticle.children)) {
      article.children = rawArticle.children.map((articleChild: any) => this.processRawArticleRecursive(articleChild, imageIds, articles));
    }

    this.processRefTokens(rawArticle, article);

    // Store article.
    this.storage.putArticle(article);

    // Collect image ids.
    if (Array.isArray(rawArticle.ext)) {
      rawArticle.ext.forEach((token: string) => this.processExtToken(token, imageIds, article.id));
    }

    return article;
  }

  protected processRefTokens(rawArticle: any, article: Article): void {
    // Start with raw text value in text. Then process text.
    article.text = article.rawText;
    if (Array.isArray(rawArticle.ref)) {
      rawArticle.ref.forEach((refToken: RefToken) => this.processRefToken(refToken, article))
    }
  }

  protected processRefToken(refToken: any, article: Article): void {
    article.text = article.text.replace(`[REF=${refToken.id}]`, '<em><a data-link ="' + refToken.id + '">' + refToken.label + '</a></em>')
  }

  protected processExtToken(extToken: string, imageIds: ImageIdArray, id: string) {
    if (typeof imageIds[extToken] == 'undefined') {
      imageIds[extToken] = {};
    }

    // Remember this image is used in this article.
    imageIds[extToken][id] = id;
  }



  // Image methods.
  /////////////////

  /**
   * Image.
   */
  public getImage(id: number): Observable<RBImage> {
    // Load index item from cache.
    let image = this.storage.getImage(id);

    if (image) {
      this.logger.log(`Return image "${id}" from storage`, image)
      return of(image);
    }
    else {
      // Load image from api.
      this.logger.log(`Fetch image "${id}" from server`)
      return this.fetcher.getImage(id).pipe(
        map(rawImage => this.processRawImage(rawImage))
      );
    }
  }

  protected processRawImage(rawImage: any) {
    let image = new RBImage(rawImage);
    this.storage.putImage(image);
    return image;
  }


  // Search methods.
  //////////////////

  /**
   * Search.
   */
  public search(text: string, page: number): Observable<SearchResults> {

    return this.fetcher.search(text, page).pipe(
      tap(x => this.logger.error('TAP', x)),
      map(rawSearchResults => this.processRawSearchResults(rawSearchResults))
    );
  }

  protected processRawSearchResults(rawSearchResults: any): SearchResults {
    return new SearchResults(rawSearchResults);
  }



  protected processError(error: any) {
    this.logger.error(`Error in ${this.constructor.name} (F4E6B011)`);
  }


  // Flying page methods.
  ///////////////////////
  public getFlyingPage(id: string): Observable<Article> {
    // Load index item from cache.
    let article = this.storage.getFlyingPage(id);

    if (article) {
      this.logger.log(`Return article "${id}" from storage`, article)
      return of(article);
    }
    else {
      // Load article from api.
      this.logger.log(`Fetch index "${id}" from server`, article)
      return this.fetcher.getFlyingPage(id).pipe(
        map(rawArticle => this.processRawFlyingPage(rawArticle))
      );
    }
  }

  protected processRawFlyingPage(rawFlyingPage): Article {

    let flyingPage = new Article(rawFlyingPage);

    this.storage.putFlyingPage(flyingPage);

    return flyingPage;
  }
  public getInterpretations(): Observable<Array<Interpretations>> {
    return this.fetcher.getInterpretations();
  }
}
